import { isEmpty, cloneDeep } from 'lodash';
import { ref } from '@vue/composition-api';
import hash from 'object-hash';
import QueryAPI from 'src/api/query';
import QueryUtils from 'src/utils/query';
import { isQueryValid as isValid } from 'src/components/project/analysis/results/query/utils';
export const fetchThemes = async (projectId, analysisId) => {
    return QueryAPI.listSavedQueries(projectId, analysisId);
};
export const useLocalStorageState = () => {
    const stateToLocalStorage = (state) => {
        const base64 = window.btoa(JSON.stringify(state));
        localStorage.setItem('theme-builder-state', base64);
    };
    const localStorageToState = () => {
        const state = localStorage.getItem('theme-builder-state');
        if (state) {
            try {
                const decoded = window.atob(state);
                const parsed = JSON.parse(decoded);
                return parsed;
            }
            catch {
                return {};
            }
        }
    };
    return {
        stateToLocalStorage,
        localStorageToState,
    };
};
export const makeKey = (obj) => {
    return hash(JSON.stringify(obj), {
        algorithm: 'md5',
    });
};
export const useCache = (options) => {
    const config = {
        cacheTime: Infinity,
        ...options,
    };
    const state = ref({});
    const add = (key, value) => {
        const keyHash = makeKey(key);
        state.value[keyHash] = {
            expires: Date.now() + config.cacheTime,
            value,
        };
    };
    const remove = (key) => {
        const keyHash = makeKey(key);
        delete state.value[keyHash];
    };
    const has = (key) => {
        const keyHash = makeKey(key);
        const entry = state.value[keyHash];
        return !!(entry && entry.expires > Date.now());
    };
    const get = (key) => {
        const keyHash = makeKey(key);
        const entry = state.value[keyHash];
        return entry?.value;
    };
    return {
        state: state.value,
        add,
        remove,
        has,
        get,
    };
};
export const useFetchData = () => {
    const cache = useCache();
    const fetch = (_key, fn, params = []) => {
        const key = cloneDeep(_key);
        if (cache.has(key)) {
            return Promise.resolve(cache.get(key));
        }
        return new Promise((resolve, reject) => {
            fn(...params).then((data) => {
                resolve(data);
                cache.add(key, data);
            }, reject);
        });
    };
    return {
        fetch,
    };
};
export const getQueryRows = (query_value) => {
    try {
        if (isEmpty(query_value))
            return [];
        return QueryUtils.botanicToQueryRows(query_value);
    }
    catch {
        return [];
    }
};
export const isQueryValid = (rows) => {
    return (rows.length > 0
        && isValid(rows));
};
export const getContainedQueries = (savedQuery, allSavedQueries) => {
    const containedQueries = [];
    const findNestedQueries = (queryRows) => {
        queryRows.forEach((row) => {
            if (row.type === 'query') {
                row.values?.forEach((nestedQueryIdStr) => {
                    const nestedQueryId = parseInt(nestedQueryIdStr, 10);
                    const nestedQuery = allSavedQueries.find(q => q.id === nestedQueryId);
                    if (nestedQuery) {
                        containedQueries.push(nestedQuery);
                        // Recursive check for more nested queries
                        const nestedQueryRows = QueryUtils.botanicToQueryRows(nestedQuery.query_value);
                        findNestedQueries(nestedQueryRows);
                    }
                });
            }
        });
    };
    // Start with the provided savedQuery to find nested queries
    const initialQueryRows = QueryUtils.botanicToQueryRows(savedQuery.query_value);
    findNestedQueries(initialQueryRows);
    return containedQueries;
};
// Generate a new theme name that is not already in use
export const makeNewThemeName = (themes) => {
    const themeNames = themes.map((t) => t.name);
    const newThemeName = 'New Theme';
    const newThemeNameWithNumber = (n) => `${newThemeName} ${n}`;
    let i = 1;
    while (themeNames.includes(newThemeNameWithNumber(i))) {
        i++;
    }
    return newThemeNameWithNumber(i);
};
// Extract concepts from a query
export const getConceptsFromQuery = (query) => {
    const queryRows = QueryUtils.botanicToQueryRows(query.query_value);
    const concepts = queryRows.filter((row) => row.type === 'text');
    return concepts.flatMap((c) => c.values);
};
const matchParams = (node, params) => {
    const keys = Object.keys(params);
    return keys.every((key) => params[key] === node[key]);
};
export const findNode = (nodes, params) => {
    for (const node of nodes) {
        if (node.children) {
            const found = findNode(node.children, params);
            if (found) {
                return found;
            }
        }
        if (matchParams(node, params)) {
            return node;
        }
    }
};
export function findParentNode(nodes, params) {
    for (const node of nodes) {
        if (node.children) {
            const foundChild = node.children.find((child) => matchParams(child, params));
            if (foundChild) {
                return node;
            }
            else {
                // Recursively search children
                const foundInChild = findParentNode(node.children, params);
                if (foundInChild)
                    return foundInChild;
            }
        }
    }
}
